/**
 * @file: useDeliveryCheckDirtyCheck.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useUserLocalSettings } from '@euler/app/flows/settings/functions/useUserLocalSettings';
import { DeliveryCheckManagerBase } from '@euler/app/flows/task/functions/task-detail/delivery-check/DeliveryCheckManagerBase';
import { useAppLoading } from '@euler/components/loading/Provider';
import { useShowAlert } from '@euler/functions/useShowAlert';
import { delayed } from '@euler/utils';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { NavigationAction, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useEffect, useRef } from 'react';

type BeforeRemoveEvent = {
  preventDefault: () => void;
  data: {
    action: NavigationAction;
  };
};

export const useDeliveryCheckDirtyCheck = (
  manager: DeliveryCheckManagerBase,
) => {
  const [{ noAlertBeforeNavigateAwayDeliveryCheck }, updateSettings] =
    useUserLocalSettings();

  const navigation = useNavigation<StackNavigationProp<any>>();
  const appLoading = useAppLoading();
  const alert = useShowAlert();

  const [dirty] = useBehaviorSubject(manager.dirty$);
  const actionRef = useRef<NavigationAction>();

  const continueWithAction = useCallback(
    (delay = 0) => {
      delayed(() => {
        manager.cleanUp();
        if (actionRef.current) {
          navigation.dispatch(actionRef.current);
        }
      }, delay);
    },
    [manager, navigation],
  );

  const onSaveAndContinue = useCallback(async () => {
    await appLoading.showWithAction(async () => {
      await manager.save({ saveOnly: true });
      continueWithAction(150);
    });
  }, [appLoading, continueWithAction, manager]);

  const onContinueWithoutSaving = useCallback(() => {
    continueWithAction();
  }, [continueWithAction]);

  const onContinueWithoutSavingAndDisableAlert = useCallback(() => {
    updateSettings({ noAlertBeforeNavigateAwayDeliveryCheck: true });
    continueWithAction();
  }, [continueWithAction, updateSettings]);

  const onBeforeNavigateAway = usePersistFn(async (e: BeforeRemoveEvent) => {
    actionRef.current = e.data.action;
    e.preventDefault();

    if (!dirty || noAlertBeforeNavigateAwayDeliveryCheck) {
      continueWithAction();
      return;
    }

    alert({
      title: '提示',
      message:
        '数据已修改，您所录入的信息已保存在本地缓存中，返回后并不会丢失，' +
        '建议您及时保存数据，是否现在保存？',
      buttons: [
        {
          text: '保存并返回',
          style: 'destructive',
          onPress: onSaveAndContinue,
        },
        {
          text: '直接返回',
          style: 'default',
          onPress: onContinueWithoutSaving,
        },
        {
          text: '直接返回, 以后不再提示',
          style: 'default',
          onPress: onContinueWithoutSavingAndDisableAlert,
        },
        {
          text: '取消',
          style: 'cancel',
        },
      ],
    });
  });

  useEffect(() => {
    return navigation.addListener('beforeRemove', onBeforeNavigateAway);
  }, [navigation, onBeforeNavigateAway]);
};
